import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import type { Approver } from '../ApprovalsContext.tsx';
import { useApprovals } from '../ApprovalsContext.tsx';

import { MultiColorProgressBar } from './MultiColorProgressBar.tsx';

const countByStatus = (approvers: Approver[]) =>
	approvers.reduce(
		(counts, approver) => {
			if (approver.status === 'REVIEWER_APPROVED') {
				counts.approved++;
			} else if (approver.status === 'REVIEWER_DENIED') {
				counts.denied++;
			}
			counts.total++;
			return counts;
		},
		{ approved: 0, denied: 0, total: 0 },
	);

const i18n = defineMessages({
	approvedLabel: {
		id: 'approvals.progress-bar.approved-label',
		defaultMessage: '{count} approved',
		description: 'Tooltip label for the approved segment of the approvals progress bar',
	},
	deniedLabel: {
		id: 'approvals.progress-bar.denied-label',
		defaultMessage: '{count} denied',
		description: 'Tooltip label for the denied segment of the approvals progress bar',
	},
	ariaLabel: {
		id: 'approvals.progress-bar.aria-label',
		defaultMessage: 'Approvals progress',
		description: 'Accessible label for the approvals progress bar',
	},
});

export const ApprovalsProgressBar = () => {
	const { approvalRequest } = useApprovals();
	const { formatMessage } = useIntl();

	const approvers = approvalRequest?.review?.approvers;

	const { approved, denied, total } = useMemo(() => countByStatus(approvers ?? []), [approvers]);

	return (
		<MultiColorProgressBar
			segments={[
				{
					count: approved,
					color: token('color.icon.success'),
					label: formatMessage(i18n.approvedLabel, { count: approved }),
				},
				{
					count: denied,
					color: token('color.icon.accent.gray'),
					label: formatMessage(i18n.deniedLabel, { count: denied }),
				},
			]}
			total={total}
			ariaLabel={formatMessage(i18n.ariaLabel)}
		/>
	);
};
